import {
  AnyPgColumn,
  boolean,
  date,
  foreignKey,
  index,
  pgTable,
  text,
  jsonb,
  time,
  timestamp,
  uuid,
  varchar,
  pgEnum,
  integer,
} from 'drizzle-orm/pg-core';
import {
  customer_category,
  workflow_kind,
} from '@trashlab/api/src/domains/core/schemas/base-template';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { payment_method } from '@trashlab/api/src/domains/billing/schemas/payment-method';
import { shop_configuration } from '@trashlab/api/src/domains/consumer/schemas/shop-configuration';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { recurrence_settings } from '@trashlab/api/src/domains/orders/schemas/recurrence-settings';
import { subscription_settings } from '@trashlab/api/src/domains/subscription/schemas/subscription-settings';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { customer_segment } from '@trashlab/api/src/domains/pricing/schemas/customer-segment';
import { customer_contact } from '@trashlab/api/src/domains/customer/schemas/customer-contact';

export const consumer_cart_segment = pgEnum('consumer_cart_segment', [
  'HOMEOWNER',
  'CONTRACTOR',
]);

export const consumer_cart_status = pgEnum('consumer_cart_status', [
  'PENDING',
  'SUBMITTED',
  'ACCEPTED',
  'CANCELED',
]);

export const consumer_cart_version = pgEnum('consumer_cart_version', [
  'V1',
  'V2',
  'V3',
]);

// New enum defining which shop flow created the cart (and desired service segment)
export const consumer_cart_shop_segment = pgEnum('consumer_cart_shop_segment', [
  'ROLLOFF',
  'COMMERCIAL',
  'RESIDENTIAL',
  'PORTABLE_TOILET',
]);

export const consumer_cart_source = pgEnum('consumer_cart_source', [
  'VITEAPP',
  'SITESAPP',
]);

export const checkout_identity_decision = pgEnum('checkout_identity_decision', [
  'UNDECIDED',
  'REUSE_CUSTOMER',
  'CREATE_NEW_CUSTOMER',
]);

export const consumer_cart = pgTable(
  'consumer_cart',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    publicCartId: uuid('publicCartId').defaultRandom().notNull(),
    customerAddress: jsonb('customerAddress').$type<PgAddress>(),
    customerEmail: varchar('customerEmail', { length: 255 }),
    customerName: varchar('customerName', { length: 255 }),
    customerPhoneNumber: varchar('customerPhoneNumber', { length: 255 }),
    checkoutVerifiedEmail: varchar('checkoutVerifiedEmail', { length: 255 }),
    checkoutVerifiedEmailAtTs: timestamp('checkoutVerifiedEmailAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    checkoutVerifiedPhoneNumber: varchar('checkoutVerifiedPhoneNumber', {
      length: 255,
    }),
    checkoutVerifiedPhoneNumberAtTs: timestamp(
      'checkoutVerifiedPhoneNumberAtTs',
      {
        withTimezone: true,
        mode: 'string',
      },
    ),
    checkoutIdentityDecision: checkout_identity_decision(
      'checkoutIdentityDecision',
    )
      .default('UNDECIDED')
      .notNull(),
    deliveryPreferredTimeEnd: time('deliveryPreferredTimeEnd'),
    deliveryPreferredTimeStart: time('deliveryPreferredTimeStart'),
    deliveryServiceDate: date('deliveryServiceDate'),
    materialId: uuid('materialId'),
    pickupPreferredTimeEnd: time('pickupPreferredTimeEnd'),
    pickupPreferredTimeStart: time('pickupPreferredTimeStart'),
    pickupServiceDate: date('pickupServiceDate'),
    productId: uuid('productId'),
    status: consumer_cart_status('status').default('PENDING').notNull(),
    createdBy: uuid('createdBy'),
    workflowKind: workflow_kind('workflowKind').default('DELIVERY').notNull(),
    customerNotes: text('customerNotes'),
    declineReason: text('declineReason'),
    segment: consumer_cart_segment('segment').default('HOMEOWNER').notNull(),
    notifiedHaulerAtTs: timestamp('notifiedHaulerAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    skuId: uuid('skuId'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    customerId: uuid('customerId'),
    customerContactId: uuid('customerContactId'),
    customerSiteId: uuid('customerSiteId'),
    currentStepKey: varchar('currentStepKey', { length: 255 }),
    shopId: uuid('shopId'),
    siteAddress: jsonb('siteAddress').$type<PgAddress>(),
    siteContactEmail: varchar('siteContactEmail', { length: 255 }),
    siteContactName: varchar('siteContactName', { length: 255 }),
    siteContactPhoneNumber: varchar('siteContactPhoneNumber', { length: 255 }),
    siteDetailsSameAsCustomer: boolean('siteDetailsSameAsCustomer')
      .default(true)
      .notNull(),
    wasteGeneratorCategory: customer_category('wasteGeneratorCategory').default(
      'RESIDENTIAL',
    ),
    siteDetailsSameAsBilling: boolean('siteDetailsSameAsBilling')
      .default(true)
      .notNull(),
    customerBusinessName: varchar('customerBusinessName', { length: 255 }),
    paymentMethodId: uuid('paymentMethodId'),
    cartVersion: consumer_cart_version('cartVersion').default('V2').notNull(),
    // Indicates which shop/segment this cart belongs to (defaults to ROLLOFF for legacy)
    shopSegment: consumer_cart_shop_segment('shopSegment').default('ROLLOFF'),
    contractTemplateId: uuid('contractTemplateId'),
    signedFirstName: varchar('signedFirstName', { length: 255 }),
    signedLastName: varchar('signedLastName', { length: 255 }),
    signedIp: varchar('signedIp', { length: 255 }),
    signedStyle: varchar('signedStyle', { length: 255 }),
    signedAtTs: timestamp('signedAtTs', {
      withTimezone: true,
      mode: 'string',
    }),
    daysOver: integer('daysOver').default(0),
    containerRotation: integer('containerRotation').default(0),
    // Indicates customer already has a container and does not need a delivery
    noContainerRentalNeeded: boolean('noContainerRentalNeeded').default(false),
    customerSegmentId: uuid('customerSegmentId').references(
      () => customer_segment.id,
    ),
    isAIGenerated: boolean('isAIGenerated').default(false),
    source: consumer_cart_source('source').default('VITEAPP').notNull(),
    /** UUID of the subscription settings that define billing behavior for this cart */
    subscriptionSettingsId: uuid('subscriptionSettingsId'),
    /** UUID of the recurrence settings selected for this cart */
    recurrenceSettingsId: uuid('recurrenceSettingsId').references(
      (): AnyPgColumn => recurrence_settings.id,
    ),
  },
  (table) => {
    return {
      site_merge_page_idx: index('consumer_cart_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index('consumer_cart_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      public_cart_id_idx: index('consumer_cart_public_cart_id_idx').on(
        table.publicCartId,
      ),
      status_idx: index('consumer_cart_status_idx').on(
        table.tenantId,
        table.status,
      ),
      tenantId_idx: index('consumer_cart_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('consumer_cart_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('consumer_cart_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('consumer_cart_versionNumber_idx').on(
        table.versionNumber,
      ),

      customerId_idx: index('consumer_cart_customerId_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index(
        'consumer_cart_customer_merge_page_idx',
      ).on(table.tenantId, table.customerId, table.id),
      customerContactId_idx: index('consumer_cart_customerContactId_idx').on(
        table.customerContactId,
      ),
      customerSiteId_idx: index('consumer_cart_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      materialId_idx: index('consumer_cart_materialId_idx').on(
        table.materialId,
      ),
      paymentMethodId_idx: index('consumer_cart_paymentMethodId_idx').on(
        table.paymentMethodId,
      ),
      productId_idx: index('consumer_cart_productId_idx').on(table.productId),
      shopId_idx: index('consumer_cart_shopId_idx').on(table.shopId),
      skuId_idx: index('consumer_cart_skuId_idx').on(table.skuId),
      subscriptionSettingsId_idx: index(
        'consumer_cart_subscriptionSettingsId_idx',
      ).on(table.subscriptionSettingsId),
      recurrenceSettingsId_idx: index(
        'consumer_cart_recurrenceSettingsId_idx',
      ).on(table.recurrenceSettingsId),
      shopSegment_idx: index('consumer_cart_shopSegment_idx').on(
        table.shopSegment,
      ),

      //foreign keys
      consumer_cart_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'consumer_cart_customerId_fkey',
      }),
      consumer_cart_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'consumer_cart_customerSiteId_fkey',
      }),
      consumer_cart_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'consumer_cart_materialId_fkey',
      }),
      consumer_cart_paymentMethodId_fkey: foreignKey({
        columns: [table.paymentMethodId],
        foreignColumns: [payment_method.id],
        name: 'consumer_cart_paymentMethodId_fkey',
      }),
      consumer_cart_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'consumer_cart_productId_fkey',
      }),
      consumer_cart_shopId_fkey: foreignKey({
        columns: [table.shopId],
        foreignColumns: [shop_configuration.id],
        name: 'consumer_cart_shopId_fkey',
      }),
      consumer_cart_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'consumer_cart_skuId_fkey',
      }),
      consumer_cart_subscriptionSettingsId_fkey: foreignKey({
        columns: [table.subscriptionSettingsId],
        foreignColumns: [subscription_settings.id],
        name: 'consumer_cart_subscriptionSettingsId_fkey',
      }),
      consumer_cart_recurrenceSettingsId_fkey: foreignKey({
        columns: [table.recurrenceSettingsId],
        foreignColumns: [recurrence_settings.id],
        name: 'consumer_cart_recurrenceSettingsId_fkey',
      }),
      consumer_cart_customerContactId_fkey: foreignKey({
        columns: [table.customerContactId],
        foreignColumns: [customer_contact.id],
        name: 'consumer_cart_customerContactId_fkey',
      }),
    };
  },
);

export const consumer_cart_history = pgTable('consumer_cart_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  publicCartId: uuid('publicCartId'),
  customerAddress: jsonb('customerAddress').$type<PgAddress>(),
  customerEmail: varchar('customerEmail', { length: 255 }),
  customerName: varchar('customerName', { length: 255 }),
  customerPhoneNumber: varchar('customerPhoneNumber', { length: 255 }),
  checkoutVerifiedEmail: varchar('checkoutVerifiedEmail', { length: 255 }),
  checkoutVerifiedEmailAtTs: timestamp('checkoutVerifiedEmailAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  checkoutVerifiedPhoneNumber: varchar('checkoutVerifiedPhoneNumber', {
    length: 255,
  }),
  checkoutVerifiedPhoneNumberAtTs: timestamp(
    'checkoutVerifiedPhoneNumberAtTs',
    {
      withTimezone: true,
      mode: 'string',
    },
  ),
  checkoutIdentityDecision: checkout_identity_decision(
    'checkoutIdentityDecision',
  ),
  deliveryPreferredTimeEnd: time('deliveryPreferredTimeEnd'),
  deliveryPreferredTimeStart: time('deliveryPreferredTimeStart'),
  deliveryServiceDate: date('deliveryServiceDate'),
  materialId: uuid('materialId'),
  pickupPreferredTimeEnd: time('pickupPreferredTimeEnd'),
  pickupPreferredTimeStart: time('pickupPreferredTimeStart'),
  pickupServiceDate: date('pickupServiceDate'),
  productId: uuid('productId'),
  status: consumer_cart_status('status'),
  createdBy: uuid('createdBy'),
  workflowKind: workflow_kind('workflowKind'),
  customerNotes: text('customerNotes'),
  declineReason: text('declineReason'),
  segment: consumer_cart_segment('segment'),
  notifiedHaulerAtTs: timestamp('notifiedHaulerAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  skuId: uuid('skuId'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  customerId: uuid('customerId'),
  customerContactId: uuid('customerContactId'),
  customerSiteId: uuid('customerSiteId'),
  currentStepKey: varchar('currentStepKey', { length: 255 }),
  shopId: uuid('shopId'),
  siteAddress: jsonb('siteAddress').$type<PgAddress>(),
  siteContactEmail: varchar('siteContactEmail', { length: 255 }),
  siteContactName: varchar('siteContactName', { length: 255 }),
  siteContactPhoneNumber: varchar('siteContactPhoneNumber', { length: 255 }),
  siteDetailsSameAsCustomer: boolean('siteDetailsSameAsCustomer'),
  wasteGeneratorCategory: customer_category('wasteGeneratorCategory'),
  siteDetailsSameAsBilling: boolean('siteDetailsSameAsBilling'),
  customerBusinessName: varchar('customerBusinessName', { length: 255 }),
  paymentMethodId: uuid('paymentMethodId'),
  cartVersion: consumer_cart_version('cartVersion'),
  shopSegment: consumer_cart_shop_segment('shopSegment'),
  contractTemplateId: uuid('contractTemplateId'),
  signedFirstName: varchar('signedFirstName', { length: 255 }),
  signedLastName: varchar('signedLastName', { length: 255 }),
  signedIp: varchar('signedIp', { length: 255 }),
  signedStyle: varchar('signedStyle', { length: 255 }),
  signedAtTs: timestamp('signedAtTs', {
    withTimezone: true,
    mode: 'string',
  }),
  daysOver: integer('daysOver'),
  containerRotation: integer('containerRotation'),
  noContainerRentalNeeded: boolean('noContainerRentalNeeded'),
  customerSegmentId: uuid('customerSegmentId'),
  isAIGenerated: boolean('isAIGenerated'),
  source: consumer_cart_source('source'),
  // Mirror selected configuration ids in history
  subscriptionSettingsId: uuid('subscriptionSettingsId'),
  recurrenceSettingsId: uuid('recurrenceSettingsId'),
});
